import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  Component,
  HostListener,
  ChangeDetectorRef,
  HostBinding,
  Input,
  ContentChildren,
  QueryList,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectionStrategy,
  OnInit,
  InjectionToken,
  OnDestroy,
  SkipSelf,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  GPU_MEDIA_QUERIES,
  GpuMediaQueryBreakpoints,
} from '@geneplanet/ngx-ui/src/lib/layout';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge } from 'rxjs';
import { map } from 'rxjs/operators';
import { LayoutService } from 'src/app/core/services/layout.service';
import { AnalysisHeaderTabComponent } from './components/tab/tab.component';
import { CommonModule } from '@angular/common';

export const ANALYSIS_HEADER = new InjectionToken<AnalysisHeaderComponent>(
  'AnalysisHeader'
);

@UntilDestroy()
@Component({
  selector: 'app-analysis-header',
  templateUrl: './analysis-header.component.html',
  styleUrls: ['./analysis-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: ANALYSIS_HEADER, useValue: AnalysisHeaderComponent }],
  imports: [CommonModule],
  standalone: true,
})
export class AnalysisHeaderComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @HostBinding('class') class = 'analysis-header';
  @HostBinding('style.top.px') top = this.layoutService.getHeaderHeight();
  @HostBinding('style.height.px') height: number;

  @Input() isFluidContainer = false;
  @Input() containerClass: string;
  @Input() hideBack = false;
  @Output() heightChanged = new EventEmitter();

  @ContentChildren(AnalysisHeaderTabComponent, { descendants: true })
  tabList: QueryList<AnalysisHeaderTabComponent>;
  @ViewChild('headerContainer') headerContainer: ElementRef;
  @ViewChild('titleContent') titleContent: ElementRef;
  @ViewChild('scrollRef', { static: true }) scrollRef: ElementRef;

  scrolled = false;
  isXlUp = false;
  observer: ResizeObserver;

  private activeTab: number;

  constructor(
    @SkipSelf() private parentCdr: ChangeDetectorRef, //this is needed because height change is too slow with normal cdr
    private cdr: ChangeDetectorRef,
    private layoutService: LayoutService,
    private breakpointObserver: BreakpointObserver
  ) {}

  @HostListener('window:scroll')
  windowScroll() {
    this.onScroll();
  }

  ngOnInit() {
    this.layoutService.headerHeightChange$
      .pipe(untilDestroyed(this))
      .subscribe((height: number) => (this.top = height));

    this.breakpointObserver
      .observe(GPU_MEDIA_QUERIES[GpuMediaQueryBreakpoints.XL_UP])
      .pipe(untilDestroyed(this))
      .subscribe((value: BreakpointState) => {
        this.isXlUp = value.matches;
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit() {
    this.heightObserver();
    this.activeTab = this.tabList.toArray().findIndex((t) => t.active);
    this.scrollTabs(this.activeTab);
    this.setTabSelectedListener();
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.unobserve(this.headerContainer?.nativeElement);
    }
  }

  hasTabs() {
    return this.tabList?.length > 0;
  }

  updateHeight() {
    this.height = this.headerContainer?.nativeElement.offsetHeight;
    this.parentCdr.detectChanges();
    this.heightChanged.emit();
  }

  private heightObserver(): void {
    this.observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      if (entries[0].contentRect.height !== this.height) {
        this.updateHeight();
      }
    });

    this.observer.observe(this.headerContainer?.nativeElement);
  }

  private setTabSelectedListener(): void {
    merge(...this.tabList.map((t, i) => t.selected.pipe(map(() => i))))
      .pipe(untilDestroyed(this))
      .subscribe((index: number) => {
        this.scrollTabs(index);
        this.activeTab = index;
      });
  }

  private scrollTabs(index: number): void {
    if (index === this.activeTab) {
      return;
    }

    const tabWidths = [
      ...this.scrollRef.nativeElement.children[0].children,
    ].map((x) => x.offsetWidth);

    const offset = tabWidths.reduce((a, b, i) => {
      return i < index - 1 ? a + b : a;
    }, 0);

    this.scrollRef.nativeElement.scrollTo({
      left: offset,
      behavior: 'smooth',
    });
  }

  private onScroll() {
    const mainContentHeight = document.getElementById('content').offsetHeight;
    const titleHeight = this.titleContent?.nativeElement?.offsetHeight;
    const canBeScrolled =
      this.scrolled ||
      mainContentHeight + this.layoutService.getHeaderHeight() >
        window.innerHeight + titleHeight;

    const previousValue = this.scrolled;
    this.scrolled = canBeScrolled && !!window.scrollY;

    if (previousValue != this.scrolled) {
      setTimeout(() => this.updateHeight());
    }
  }
}
